import gql from 'graphql-tag';
import { useCallback, useState } from 'react';
import { Plural, Trans, useLingui } from '@lingui/react/macro';
import { Tooltip } from '@mui/material';
import Box from '@mui/material/Box';
import Chip from '@mui/material/Chip';
import IconButton from '@mui/material/IconButton';
import Stack from '@mui/material/Stack';
import Typography from '@mui/material/Typography';

import { Flags } from '@watershed/constants/flags';
import AddIcon from '@watershed/icons/components/Add';
import BroomIcon from '@watershed/icons/components/Broom';
import BrowserRefreshIcon from '@watershed/icons/components/BrowserRefresh';
import DeleteIcon from '@watershed/icons/components/Delete';
import LockKeyhole from '@watershed/icons/components/LockKeyhole';
import PencilIcon from '@watershed/icons/components/Pencil';
import PrivateIcon from '@watershed/icons/components/Private';
import SearchIcon from '@watershed/icons/components/Search';
import StatusCompleteIcon from '@watershed/icons/components/StatusComplete';
import StatusMissingIcon from '@watershed/icons/components/StatusMissing';
import UnlockKeyhole from '@watershed/icons/components/UnlockKeyhole';
import useLocale from '@watershed/intl/frontend/useLocale';
import OverflowChipList from '@watershed/shared-frontend/components/OverflowChipList';
import useSnackbar from '@watershed/shared-frontend/hooks/useSnackbar';
import { TestIds } from '@watershed/shared-universal/utils/testUtils';
import mixinSx from '@watershed/style/mixinSx';
import { tooltipDarkStyleProps } from '@watershed/style/tooltips';
import BlankSlate from '@watershed/ui-core/components/BlankSlate';
import Button from '@watershed/ui-core/components/Button';
import {
  GridColDef,
  GridToolbarQuickFilter,
} from '@watershed/ui-core/components/DataGrid/DataGrid';
import DateChip from '@watershed/ui-core/components/DateChip';
import { useDialogConfirm } from '@watershed/ui-core/components/DialogConfirm';
import { ObjectList } from '@watershed/ui-core/components/ObjectList/ObjectList';
import { getOverflowMenuColumn } from '@watershed/ui-core/components/ObjectList/ObjectListOverflowMenu';

import { GQOauthClientForApiKeyPageFragment } from '../../../generated/graphql-operations';
import {
  useDeleteOauthClientMutation,
  useRevokeOauthClientTokensMutation,
} from '../../../generated/urql';
import { useFeatureFlag } from '../../../utils/FeatureFlag';
import RegenerateOAuthClientSecretDialog from '../dialogs/RegenerateOAuthClientSecretDialog';
import UpdateApiOAuthClientDialog from '../dialogs/UpdateApiOAuthClientDialog';
import { objectListContainerStyles, pageDataGridStyles } from '../utils/styles';
import { AuthMethodDropdown } from './AuthMethodDropdown';
import { msToMinutes } from './oauthClientDurationUtils';

gql`
  mutation DeleteOauthClient($input: DeleteOauthClientInput!)
  @withOwner(owner: EnterpriseFoundations) {
    deleteOauthClient(input: $input) {
      success
    }
  }

  mutation RevokeOauthClientTokens($input: RevokeOauthClientTokensInput!)
  @withOwner(owner: EnterpriseFoundations) {
    revokeOauthClientTokens(input: $input) {
      success
      client {
        id
        activeApiKeys {
          id
          createdAt
          expiresAt
        }
      }
    }
  }

  mutation RegenerateOauthClientSecret(
    $input: RegenerateOauthClientSecretInput!
  ) @withOwner(owner: EnterpriseFoundations) {
    regenerateOauthClientSecret(input: $input) {
      client {
        id
        name
        description
        secretSuffix
        activeApiKeys {
          id
          createdAt
          expiresAt
        }
      }
      secret
    }
  }
`;

// Move Toolbar outside to prevent recreation on every render
function Toolbar({
  selectedAuthMethod,
  setSelectedAuthMethod,
  hasOAuthClients,
}: {
  selectedAuthMethod: 'token' | 'oauth';
  setSelectedAuthMethod: (method: 'token' | 'oauth') => void;
  hasOAuthClients: boolean;
}) {
  const { t } = useLingui();
  const isOAuthEnabled = useFeatureFlag(Flags.EntFoundEnableOAuth);

  return (
    <Stack direction="row" component="header" justifyContent="space-between">
      <GridToolbarQuickFilter
        sx={{ flexGrow: 0.15, minWidth: 256 }}
        placeholder={t({
          message: 'Search…',
          context: 'Placeholder for the credentials search bar',
        })}
      />
      {isOAuthEnabled && (
        <AuthMethodDropdown
          selectedAuthMethod={selectedAuthMethod}
          setSelectedAuthMethod={setSelectedAuthMethod}
          hasOAuthClients={hasOAuthClients}
          hasApiKeys={false}
        />
      )}
    </Stack>
  );
}

export const ApiOauthClientGridTestIds = {
  NoRowsOverlay: 'api-oauth-client-grid-no-rows-overlay',
  NoResultsOverlay: 'api-oauth-client-grid-no-results-overlay',
  CreateButton: 'api-oauth-client-grid-create-button',
  EditButton: 'api-oauth-client-grid-edit-button',
  UpdateDialog: 'api-oauth-client-grid-update-dialog',
  RegenerateDialog: 'api-oauth-client-grid-regenerate-dialog',
  RegenerateSecretButton: 'api-oauth-client-grid-regenerate-secret-button',
  RevokeTokensButton: 'api-oauth-client-grid-revoke-tokens-button',
  DeleteButton: 'api-oauth-client-grid-delete-button',
  SearchInput: 'api-oauth-client-grid-search-input',
};

export default function ApiOAuthClientGrid({
  apiOAuthClients,
  selectedAuthMethod,
  setSelectedAuthMethod,
  toggleCreateDialog,
}: {
  apiOAuthClients: Array<GQOauthClientForApiKeyPageFragment>;
  selectedAuthMethod: 'token' | 'oauth';
  setSelectedAuthMethod: (method: 'token' | 'oauth') => void;
  toggleCreateDialog: () => void;
}) {
  const { t } = useLingui();
  const locale = useLocale();
  const { enqueueSnackbar } = useSnackbar();
  const openConfirm = useDialogConfirm();
  const [visibleSecrets, setVisibleSecrets] = useState<Record<string, boolean>>(
    {}
  );

  const toggleSecretVisibility = useCallback((id: string) => {
    setVisibleSecrets((prev) => ({
      ...prev,
      [id]: !prev[id],
    }));
  }, []);
  const [updateDialogOpen, setUpdateDialogOpen] = useState(false);
  const [regenerateDialogOpen, setRegenerateDialogOpen] = useState(false);
  const [selectedClient, setSelectedClient] =
    useState<GQOauthClientForApiKeyPageFragment | null>(null);
  const [regenerateClient, setRegenerateClient] =
    useState<GQOauthClientForApiKeyPageFragment | null>(null);

  const [, deleteOauthClient] = useDeleteOauthClientMutation();
  const [, revokeOauthClientTokens] = useRevokeOauthClientTokensMutation();

  const handleEdit = useCallback(
    async (rowId: string) => {
      const client = apiOAuthClients.find((c) => c.id === rowId);
      if (client) {
        setSelectedClient(client);
        setUpdateDialogOpen(true);
      }
    },
    [apiOAuthClients]
  );

  const handleRegenerate = useCallback(
    async (rowId: string) => {
      const client = apiOAuthClients.find((c) => c.id === rowId);
      if (client) {
        setRegenerateClient(client);
        setRegenerateDialogOpen(true);
      }
    },
    [apiOAuthClients]
  );

  const handleRevokeTokens = useCallback(
    async (rowId: string) => {
      const didConfirm = await openConfirm({
        title: 'Revoke OAuth client tokens',
        message:
          'Are you sure you want to revoke all tokens for this OAuth client?',
        confirmText: 'Revoke',
      });
      if (didConfirm) {
        const result = await revokeOauthClientTokens({ input: { id: rowId } });
        if (result.error) {
          enqueueSnackbar(result.error.message, {
            variant: 'error',
          });
        } else {
          enqueueSnackbar('OAuth client tokens revoked.', {
            variant: 'success',
          });
        }
      }
    },
    [enqueueSnackbar, openConfirm, revokeOauthClientTokens]
  );

  const handleDelete = useCallback(
    async (rowId: string) => {
      const didConfirm = await openConfirm({
        title: 'Delete OAuth client',
        message: 'Are you sure you want to delete this OAuth client?',
        confirmText: 'Delete',
      });
      if (didConfirm) {
        const result = await deleteOauthClient({ input: { id: rowId } });
        if (result.error) {
          enqueueSnackbar(result.error.message, {
            variant: 'error',
          });
        } else {
          enqueueSnackbar('OAuth client deleted.', {
            variant: 'success',
          });
        }
      }
    },
    [enqueueSnackbar, openConfirm, deleteOauthClient]
  );

  const columns: Array<GridColDef<GQOauthClientForApiKeyPageFragment>> = [
    {
      field: 'name',
      headerName: t({
        message: 'Name',
        context: 'Field label',
      }),
      flex: 1,
      editable: false,
      renderCell: ({ row }) => (
        <ObjectList.TitleCell title={row.name} subtitle={row.description} />
      ),
    },
    {
      field: 'id',
      headerName: t({
        message: 'Client ID',
        context: 'Field label',
      }),
      flex: 1,
      editable: false,
      renderCell: ({ row }) => (
        <Stack
          direction="row"
          alignItems="center"
          spacing={1}
          sx={{ width: '100%' }}
        >
          <Tooltip
            slotProps={tooltipDarkStyleProps()}
            title={
              visibleSecrets[row.id]
                ? row.id
                : t({
                    message: 'Click the lock icon to reveal',
                    context: 'Tooltip for masked API client ID',
                  })
            }
          >
            <Typography
              variant="caption"
              sx={{
                overflow: 'hidden',
                textOverflow: 'ellipsis',
                whiteSpace: 'nowrap',
                maxWidth: 'calc(100% - 40px)',
                color: (theme) => theme.palette.grey50,
                fontFamily: (theme) => theme.typography.fontFamilyMonospace,
              }}
            >
              {visibleSecrets[row.id] ? row.id : 'oac_' + '•'.repeat(20)}
            </Typography>
          </Tooltip>
          <IconButton
            aria-label={`Show client ${row.id}`}
            size="small"
            onClick={() => toggleSecretVisibility(row.id)}
            sx={{
              ml: 'auto',
              border: 0,
              boxShadow: 'none',
              bgcolor: 'transparent',
              padding: '6px',
              '& svg': {
                transform: visibleSecrets[row.id] ? 'scale(1.1)' : 'scale(1)',
              },
              '&:hover': {
                backgroundColor: (theme) => theme.palette.grey20,
                color: 'inherit',
              },
              '&:focus, &:focus-visible': {
                border: 0,
                boxShadow: 'none',
                color: 'inherit',
              },
            }}
          >
            {visibleSecrets[row.id] ? (
              <UnlockKeyhole color={(theme) => theme.palette.grey50} />
            ) : (
              <LockKeyhole color={(theme) => theme.palette.grey50} />
            )}
          </IconButton>
        </Stack>
      ),
    },
    {
      field: 'secret',
      headerName: t({
        message: 'Client secret',
        context: 'Field label',
      }),
      flex: 0.75,
      editable: false,
      renderCell: ({ row }) => (
        <Tooltip
          slotProps={tooltipDarkStyleProps()}
          title={t({
            message:
              'The client secret is not available after creation. If you need to regenerate it, you can do so by clicking the regenerate button.',
            context: 'Tooltip for masked API client secret',
          })}
        >
          <Typography
            variant="body2"
            sx={{
              fontFamily: (theme) => theme.typography.fontFamilyMonospace,
              color: (theme) => theme.palette.grey50,
            }}
          >
            {'•'.repeat(10) + row.secretSuffix}
          </Typography>
        </Tooltip>
      ),
    },
    {
      field: 'roles',
      headerName: t({
        message: 'Roles',
        context: 'Field label',
      }),
      description: t({
        message: 'The roles which control the permissions that this token has',
        context: 'Field description',
      }),
      flex: 0.6,
      editable: false,
      renderCell: ({ row }) => (
        <OverflowChipList
          items={row.roles.map((record) => ({
            label: record.name,
          }))}
          itemDisplay={{
            type: 'popover',
            renderPopover: ({ items }) => (
              <Stack
                gap={1}
                flexWrap="wrap"
                sx={{
                  p: 1,
                  width: 320,
                  border: (theme) => `thin solid ${theme.palette.grey20}`,
                  borderRadius: '10px',
                }}
              >
                <Typography variant="body2">
                  {t({
                    message: 'Roles:',
                    context: 'Field label',
                  })}
                </Typography>
                <Stack direction="row" gap={1} flexWrap="wrap">
                  {items.map((item) => (
                    <Chip key={item.label} label={item.label} />
                  ))}
                </Stack>
              </Stack>
            ),
          }}
        />
      ),
    },
    {
      field: 'apiKeyExpiryMs',
      headerName: t({
        message: 'Expiration',
        context: 'Field label for the expiration time of tokens',
      }),
      description: t({
        message: 'How long tokens generated for this client will remain active',
        context: 'Field description',
      }),
      flex: 0.6,
      editable: false,
      renderCell: ({ row }) => {
        const expirationMinutes = msToMinutes(row.apiKeyExpiryMs);
        return (
          <Typography variant="body2">
            <Plural
              value={expirationMinutes}
              one="# minute"
              other="# minutes"
            />
          </Typography>
        );
      },
    },
    {
      field: 'status',
      headerName: t({
        message: 'Status',
        context: 'Field label',
      }),
      flex: 0.5,
      editable: false,
      renderCell: ({ row }) => {
        const hasActiveKeys = row.activeApiKeys.length > 0;
        const mostRecentKey = hasActiveKeys ? row.activeApiKeys[0] : null;

        return (
          <Tooltip
            title={
              mostRecentKey ? (
                <Trans context="Active OAuth client created at timestamp">
                  Latest auth token created at:{' '}
                  {mostRecentKey.createdAt.toLocaleString(locale)}
                </Trans>
              ) : (
                ''
              )
            }
          >
            <Stack
              direction="row"
              alignItems="center"
              justifyContent="center"
              gap={1}
            >
              <Stack alignItems="center" sx={{ width: 16 }}>
                {hasActiveKeys ? (
                  <StatusCompleteIcon color={(theme) => theme.palette.grass} />
                ) : (
                  <StatusMissingIcon color={(theme) => theme.palette.grey40} />
                )}
              </Stack>
              <Typography variant="body2">
                {hasActiveKeys ? (
                  <Trans context="Active OAuth client">Active</Trans>
                ) : (
                  <Trans context="Inactive OAuth client">Inactive</Trans>
                )}
              </Typography>
            </Stack>
          </Tooltip>
        );
      },
    },
    {
      field: 'createdAt',
      headerName: t({
        message: 'Created',
        context: 'The date an OAuth client was created',
      }),
      flex: 0.6,
      renderCell: ({ row }) => (
        <DateChip
          date={row.createdAt}
          sx={{
            backgroundColor: 'transparent',
          }}
        />
      ),
    },

    getOverflowMenuColumn(({ row }) => ({
      dropdownItems: [
        {
          id: 'edit',
          Icon: PencilIcon,
          label: <Trans context="Edit OAuth client">Edit</Trans>,
          onSelect: () => handleEdit(row.id),
          testId: ApiOauthClientGridTestIds.EditButton,
        },
        {
          id: 'regenerate',
          Icon: BrowserRefreshIcon,
          label: (
            <Trans context="Regenerate OAuth client">Regenerate secret</Trans>
          ),
          onSelect: () => handleRegenerate(row.id),
          testId: ApiOauthClientGridTestIds.RegenerateSecretButton,
        },
        {
          id: 'revokeTokens',
          Icon: BroomIcon,
          label: <Trans context="Revoke OAuth tokens">Revoke tokens</Trans>,
          onSelect: () => handleRevokeTokens(row.id),
          testId: ApiOauthClientGridTestIds.RevokeTokensButton,
        },
        {
          id: 'delete',
          Icon: DeleteIcon,
          variant: 'error',
          label: <Trans context="Delete OAuth client">Delete client</Trans>,
          onSelect: () => handleDelete(row.id),
          testId: ApiOauthClientGridTestIds.DeleteButton,
        },
      ],
    })),
  ];

  return (
    <Box sx={objectListContainerStyles}>
      <ObjectList<GQOauthClientForApiKeyPageFragment>
        sx={mixinSx(pageDataGridStyles, {
          '--DataGrid-overlayHeight': '300px',
        })}
        id={TestIds.ApiOauthClientGrid}
        rows={apiOAuthClients}
        columns={columns}
        disableColumnPinning
        disableAggregation
        autoHeight
        slots={{
          toolbar: Toolbar,
          noRowsOverlay: () => (
            <BlankSlate
              title={t({
                message: 'No OAuth API credentials',
                context: 'Empty state title for OAuth client list',
              })}
              icon={PrivateIcon}
              subtitle={t({
                message:
                  'Create new credentials and associate them with a role to get started.',
                context: 'Empty state subtitle for OAuth client list',
              })}
            >
              <Button
                color="primary"
                startIcon={<AddIcon />}
                onClick={() => toggleCreateDialog()}
                data-testid={ApiOauthClientGridTestIds.CreateButton}
              >
                <Trans context="Create credentials button">
                  Create credentials
                </Trans>
              </Button>
            </BlankSlate>
          ),
          noResultsOverlay: () => (
            <BlankSlate
              title={t({
                message: 'No OAuth clients found',
                context: 'Empty state message for OAuth client list',
              })}
              subtitle={t({
                message: 'No OAuth clients match your search criteria.',
                context: 'Empty state subtitle for OAuth client list',
              })}
              icon={SearchIcon}
              sx={{
                height: '100%',
                width: '100%',
              }}
            />
          ),
        }}
        slotProps={{
          toolbar: {
            selectedAuthMethod,
            setSelectedAuthMethod,
            hasOAuthClients: apiOAuthClients.length > 0,
          },
        }}
      />
      <UpdateApiOAuthClientDialog
        data-testid={ApiOauthClientGridTestIds.UpdateDialog}
        open={updateDialogOpen}
        onClose={() => {
          setUpdateDialogOpen(false);
          setSelectedClient(null);
        }}
        client={selectedClient}
      />
      <RegenerateOAuthClientSecretDialog
        data-testid={ApiOauthClientGridTestIds.RegenerateDialog}
        open={regenerateDialogOpen}
        onClose={() => {
          setRegenerateDialogOpen(false);
          setRegenerateClient(null);
        }}
        clientId={regenerateClient?.id ?? ''}
        clientName={regenerateClient?.name ?? ''}
      />
    </Box>
  );
}
//
